import api from "./index";

// REA Properties API calls
export const getREAProperties = (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append('page', params.page);
  if (params.page_size) queryParams.append('page_size', params.page_size);
  if (params.search) queryParams.append('search', params.search);
  if (params.listing_type) queryParams.append('listing_type', params.listing_type);
  if (params.category) queryParams.append('category', params.category);
  if (params.state) queryParams.append('state', params.state);
  if (params.suburb) queryParams.append('suburb', params.suburb);
  if (params.min_price) queryParams.append('min_price', params.min_price);
  if (params.max_price) queryParams.append('max_price', params.max_price);
  if (params.min_beds) queryParams.append('min_beds', params.min_beds);
  if (params.max_beds) queryParams.append('max_beds', params.max_beds);
  if (params.min_baths) queryParams.append('min_baths', params.min_baths);
  if (params.min_car_spaces) queryParams.append('min_car_spaces', params.min_car_spaces);
  if (params.method) queryParams.append('method', params.method);
  if (params.withdrawn) queryParams.append('withdrawn', params.withdrawn);
  if (params.sold) queryParams.append('sold', params.sold);

  return api.get(`property-crawler/real-estate-properties/?${queryParams.toString()}`);
};

export const getREAInquiries = (listingId, params = {}) => {
  const queryParams = new URLSearchParams();
  if (listingId) queryParams.append('listing_id', listingId);

  if (params.page) queryParams.append('page', params.page);
  if (params.page_size) queryParams.append('page_size', params.page_size);

  return api.get(`property-crawler/rea-enquiries/?${queryParams.toString()}`);
};

// Get single REA property
export const getREAProperty = (propertyId) =>
  api.get(`property-crawler/real-estate-properties/${propertyId}/`);

// Create new REA property
export const createREAProperty = (propertyData) => {
  const formData = new FormData();

  // Add all the property fields to FormData
  formData.append('property_type', propertyData.property_type);
  formData.append('sub_number', propertyData.sub_number || '');
  formData.append('street_number', propertyData.street_number || '0');
  formData.append('street', propertyData.street);
  formData.append('suburb', propertyData.suburb);
  formData.append('state', propertyData.state);
  formData.append('postcode', propertyData.postcode);
  formData.append('category', propertyData.category);
  formData.append('headline', propertyData.headline);
  formData.append('description', propertyData.description);
  formData.append('price', propertyData.price || '0');
  formData.append('bedrooms', propertyData.bedrooms || '0');
  formData.append('bathrooms', propertyData.bathrooms || '0');
  formData.append('garages', propertyData.garages || '0');
  formData.append('open_spaces', propertyData.open_spaces || '0');
  formData.append('toilets', propertyData.toilets || '0');
  if (propertyData.agent_id) formData.append('agent_id', propertyData.agent_id);
  if (propertyData.status) formData.append('status', propertyData.status);
  if (propertyData.method_of_sale) formData.append('method_of_sale', propertyData.method_of_sale);
  if (propertyData.parking_type) formData.append('parking_type', propertyData.parking_type);
  if (propertyData.parking_spaces) formData.append('parking_spaces', propertyData.parking_spaces);
  if (propertyData.floor_area) formData.append('floor_area', propertyData.floor_area);
  if (propertyData.land_area) formData.append('land_area', propertyData.land_area);
  if (propertyData.property_class) formData.append('property_class', propertyData.property_class);
  if (propertyData.area) formData.append('area', propertyData.area);
  if (propertyData.transaction_type) formData.append('transaction_type', propertyData.transaction_type);
  // Always send weekly_rent when the caller provides the key (even empty string)
  // so the backend can clear it on sale duplicates and persist it on lease duplicates.
  if (Object.prototype.hasOwnProperty.call(propertyData, 'weekly_rent')) {
    formData.append('weekly_rent', propertyData.weekly_rent ?? '');
  }

  // Append extra_info as JSON string
  if (propertyData.extra_info) {
    formData.append('extra_info', JSON.stringify(propertyData.extra_info));
  }

  // Add images if provided (maximum 5 images)
  // Send all images with the same field name 'images' (like Postman)
  if (propertyData.images && propertyData.images.length > 0) {
    const maxImages = Math.min(propertyData.images.length, 5);
    for (let i = 0; i < maxImages; i++) {
      formData.append('images', propertyData.images[i]);
    }
  }

  return api.post('property-crawler/real-estate-properties/uploads/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Check upload status
export const checkUploadStatus = (uploadId) => {
  return api.post('property-crawler/real-estate-properties/uploads-status/', {
    upload_id: uploadId
  }, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

// Update REA property
export const updateREAProperty = (propertyId, propertyData) => {
  // If the caller already built a FormData (e.g. delete/replace image flows
  // that only need delete_images), send it through unchanged. Rebuilding it
  // would drop the fields the caller explicitly set.
  if (propertyData instanceof FormData) {
    return api.put(`property-crawler/real-estate-properties/${propertyId}/`, propertyData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  const formData = new FormData();

  // Add all the property fields to FormData (matching defaults from createREAProperty)
  // property_type is validated by ReaPropertyUploaderSerializer - must be one of: residential, commercial, business, land, rural, rental, holidayRental
  formData.append('property_type', propertyData.property_type || 'residential');
  // listing_type is the model field - set it to match property_type if not explicitly provided
  if (propertyData.listing_type) {
    formData.append('listing_type', propertyData.listing_type);
  } else if (propertyData.property_type) {
    formData.append('listing_type', propertyData.property_type);
  }
  formData.append('sub_number', propertyData.sub_number || '');
  formData.append('street_number', propertyData.street_number || '0');
  if (propertyData.street) formData.append('street', propertyData.street);
  if (propertyData.suburb) formData.append('suburb', propertyData.suburb);
  if (propertyData.state) formData.append('state', propertyData.state);
  if (propertyData.postcode) formData.append('postcode', propertyData.postcode);
  if (propertyData.category) formData.append('category', propertyData.category);
  if (propertyData.headline) formData.append('headline', propertyData.headline);
  if (propertyData.description) formData.append('description', propertyData.description);

  if (propertyData.price !== undefined && propertyData.price !== null && propertyData.price !== '') {
    formData.append('price', propertyData.price);
  }
  if (propertyData.bedrooms !== undefined && propertyData.bedrooms !== null) {
    formData.append('bedrooms', propertyData.bedrooms);
  }
  if (propertyData.bathrooms !== undefined && propertyData.bathrooms !== null) {
    formData.append('bathrooms', propertyData.bathrooms);
  }
  if (propertyData.garages !== undefined && propertyData.garages !== null) {
    formData.append('garages', propertyData.garages);
  }
  if (propertyData.open_spaces !== undefined && propertyData.open_spaces !== null) {
    formData.append('open_spaces', propertyData.open_spaces);
  }
  if (propertyData.toilets !== undefined && propertyData.toilets !== null) {
    formData.append('toilets', propertyData.toilets);
  }

  if (propertyData.agent_id) formData.append('agent_id', propertyData.agent_id);
  if (propertyData.status) formData.append('status', propertyData.status);

  // Extra fields
  if (propertyData.method_of_sale) formData.append('method_of_sale', propertyData.method_of_sale);
  if (propertyData.parking_type) formData.append('parking_type', propertyData.parking_type);
  if (propertyData.parking_spaces !== undefined && propertyData.parking_spaces !== null) {
    formData.append('parking_spaces', propertyData.parking_spaces);
  }
  if (propertyData.car_ports !== undefined && propertyData.car_ports !== null) {
    formData.append('car_ports', propertyData.car_ports);
  }
  if (propertyData.open_space !== undefined && propertyData.open_space !== null) {
    formData.append('open_spaces', propertyData.open_space);
  }
  if (propertyData.floor_area) formData.append('floor_area', propertyData.floor_area);
  if (propertyData.land_area) formData.append('land_area', propertyData.land_area);
  if (propertyData.property_class) formData.append('property_class', propertyData.property_class);
  if (propertyData.area) formData.append('area', propertyData.area);
  // Weekly rent - always send so both add (empty→value) and remove (value→empty) work; N/A yields are display-only
  const weeklyRentValue = (propertyData.weekly_rent !== '' && propertyData.weekly_rent != null && propertyData.weekly_rent !== undefined)
    ? propertyData.weekly_rent
    : '';
  formData.append('weekly_rent', weeklyRentValue);

  // Bond - auto-tracks weekly rent; only send when the caller provides the key so
  // non-rental saves don't clear it. Empty string clears it (same as weekly_rent).
  if (Object.prototype.hasOwnProperty.call(propertyData, 'bond')) {
    formData.append('bond', propertyData.bond ?? '');
  }

  // New fields match create
  if (propertyData.est_gross_yield) formData.append('est_gross_yield', propertyData.est_gross_yield);
  if (propertyData.est_outgoings) formData.append('est_outgoings', propertyData.est_outgoings);
  if (propertyData.est_net_yield) formData.append('est_net_yield', propertyData.est_net_yield);
  if (propertyData.commission_type) formData.append('commission_type', propertyData.commission_type);
  if (propertyData.commission_rate) formData.append('commission_rate', propertyData.commission_rate);
  if (propertyData.commission_amount) formData.append('commission_amount', propertyData.commission_amount);
  if (propertyData.land_size) formData.append('land_size', propertyData.land_size);
  if (propertyData.building_size) formData.append('building_size', propertyData.building_size);
  if (propertyData.year_built) formData.append('year_built', propertyData.year_built);

  // Handle features JSON object (for carports, openSpaces, etc.)
  if (propertyData.features && typeof propertyData.features === 'object') {
    formData.append('features', JSON.stringify(propertyData.features));
  }

  // Handle Image URLs (Existing) - auto-merge into extra_info
  // This ensures valid URLs in 'images' array are preserved
  const existingUrls = (propertyData.images || []).filter(img => typeof img === 'string');
  let extraInfo = propertyData.extra_info || {};

  if (existingUrls.length > 0) {
    extraInfo = { ...extraInfo, images: existingUrls };
  }

  // Always append extra_info if it has content or if we added images
  if (Object.keys(extraInfo).length > 0) {
    formData.append('extra_info', JSON.stringify(extraInfo));
  }

  // Handle images: append all File objects to 'images' field (no limit; backend allows up to 20)
  // Existing URLs should be in extra_info.images (backend merges with new S3 URLs)
  if (propertyData.images && propertyData.images.length > 0) {
    for (let i = 0; i < propertyData.images.length; i++) {
      const img = propertyData.images[i];
      if (img instanceof File) {
        formData.append('images', img);
      }
    }
  }

  // Property compliance: a live listing's price change carries its reason
  // (and whether the vendor was told) so it is kept on the price history.
  ['price_change_reason', 'vendor_notified', 'vendor_notified_how'].forEach((key) => {
    const value = propertyData[key];
    if (value !== undefined && value !== null && value !== '') {
      formData.append(key, typeof value === 'boolean' ? (value ? 'true' : 'false') : value);
    }
  });

  return api.put(`property-crawler/real-estate-properties/${propertyId}/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Patch REA property (partial update for archive/unarchive)
export const patchREAProperty = (id, data) => {
  return api.patch(`property-crawler/real-estate-properties/${id}/`, data, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

// Delete REA property
export const deleteREAProperty = (propertyId) => {
  return api.delete(`property-crawler/real-estate-properties/${propertyId}/`);
};

// Sync properties from REA API
export const syncREAProperties = () => {
  return api.post('property-crawler/real-estate-properties/sync/');
};

// REA Publishing APIs for Collings/REA Properties
export const publishCollingsPropertyToREA = (propertyId) =>
  api.post(`property-crawler/real-estate-properties/${propertyId}/publish-rea/`);

export const unpublishCollingsPropertyFromREA = (propertyId) =>
  api.post(`property-crawler/real-estate-properties/${propertyId}/unpublish-rea/`);

export const previewCollingsREAXML = (propertyId) =>
  api.get(`property-crawler/real-estate-properties/${propertyId}/preview-reaxml/`);

// Reorder images for a REA property (lightweight PATCH - only updates extra_info.images)
export const reorderREAPropertyImages = (propertyId, images) =>
  api.patch(
    `property-crawler/real-estate-properties/${propertyId}/reorder-images/`,
    { images },
    { headers: { 'Content-Type': 'application/json' } }
  );

// Floorplan tagging (separate from the regular Images gallery - REA allows at
// most 2, see partner.realestate.com.au/listing-upload/elements/#floorplan).
// Floorplan status actually lives on the linked property_management.Property's
// PropertyImage rows; see RealEstatePropertyDetailView.patch's floorplan_images
// block for why.
export const uploadREAPropertyFloorplan = (propertyId, files) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('floorplan_images', file));
  return api.patch(
    `property-crawler/real-estate-properties/${propertyId}/`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
};

export const deleteREAPropertyFloorplan = (propertyId, floorplanImageIds) => {
  const formData = new FormData();
  formData.append('delete_floorplan_images', JSON.stringify(floorplanImageIds));
  return api.patch(
    `property-crawler/real-estate-properties/${propertyId}/`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
};

export const withdrawREAPropertyFromREA = (propertyId, reason, reasonNote = "") =>
  api.post(`property-crawler/real-estate-properties/${propertyId}/withdraw-from-rea/`, {
    reason,
    reason_note: reasonNote,
  });

// confirmReaSold: see publishSoldToPortals in api/agent.js.
export const publishREAPropertySoldToPortals = (propertyId, channels, { confirmReaSold = false } = {}) =>
  api.post(`property-crawler/real-estate-properties/${propertyId}/publish-sold/`, {
    channels,
    confirm_rea_sold: confirmReaSold,
  });

// Not tied to a property type — checking a REA upload's outcome only needs
// the upload_id + REA credentials, so both property pages' modals share this.
export const checkREAUploadOutcome = (uploadId) =>
  api.get(`property-crawler/rea-upload-outcome/${uploadId}/`);